/**
 * Minimal API client.
 *
 * Authentication is a session cookie set by the API, so every request sends
 * credentials and none of them carries a tenant identifier: the tenant is
 * derived from the session server-side and a client-supplied value is ignored
 * (CLAUDE.md, docs/plans/05-api-plan.md).
 */

export interface ApiErrorDetail {
  node_id?: string;
  path?: string;
  code: string;
  detail?: string;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  request_id: string;
  details?: ApiErrorDetail[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { Accept: "application/json", ...init?.headers },
  });
  const text = await response.text();
  const payload: unknown = text === "" ? null : JSON.parse(text);

  if (!response.ok) {
    const envelope = payload as { error?: ApiErrorBody } | null;
    throw new ApiError(
      response.status,
      envelope?.error ?? {
        code: "unknown",
        message: `request failed with status ${response.status}`,
        request_id: response.headers.get("X-Request-Id") ?? "",
      },
    );
  }
  return payload as T;
}

export interface HealthResponse {
  status: string;
  version: string;
}

export interface MediaReadiness {
  status: string;
  media_plane: string;
  adapter: string;
  instance_id: string;
}

export interface Identity {
  user_id: string;
  tenant_id: string;
  role: string;
  platform_admin: boolean;
}

export interface LoginResponse extends Identity {
  csrf_token: string;
}

export interface Invite {
  invite_id: string;
  username: string;
  role: string;
  created_at: string;
  expires_at: string;
}

export interface InviteCreated {
  invite_id: string;
  username: string;
  role: string;
  /** Plaintext, returned exactly once — see the API's own doc comment: there
   * is no email integration yet, so the inviting admin relays this out of
   * band. */
  token: string;
  expires_at: string;
}

export interface PasswordResetIssued {
  /** Plaintext, returned exactly once. */
  token: string;
  expires_at: string;
}

export interface FlowVersion {
  flow_version_id: string;
  name: string;
  published_at: string;
}

export interface PublishedFlow extends FlowVersion {
  program: unknown;
}

export interface Asset {
  asset_id: string;
  status: string;
  created_at: string;
}

export interface Voice {
  voice: string;
  language: string;
  created_at: string;
}

export interface AdhocCall {
  adhoc_call_id: string;
  status: "pending" | "dispatched" | "failed";
  call_id?: string;
  error?: string;
  created_at: string;
  dispatched_at?: string;
}

function authedJSON<T>(path: string, csrfToken: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify(body),
  });
}

export const api = {
  health: () => request<HealthResponse>("/healthz"),
  ready: () => request<{ status: string }>("/readyz"),
  mediaReady: () => request<MediaReadiness>("/readyz/media"),
  me: () => request<Identity>("/v1/auth/me"),
  login: (username: string, password: string) =>
    request<LoginResponse>("/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    }),
  logout: (csrfToken: string) =>
    request<{ status: string }>("/v1/auth/logout", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
    }),
  listInvites: () => request<{ invites: Invite[] }>("/v1/invites"),
  createInvite: (csrfToken: string, username: string, role: string) =>
    authedJSON<InviteCreated>("/v1/invites", csrfToken, { username, role }),
  revokeInvite: (csrfToken: string, inviteId: string) =>
    authedJSON<{ status: string }>(`/v1/invites/${encodeURIComponent(inviteId)}/revoke`, csrfToken, {}),
  acceptInvite: (token: string, password: string) =>
    request<LoginResponse>("/v1/invites/accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    }),
  requestPasswordReset: (csrfToken: string, username: string) =>
    authedJSON<PasswordResetIssued>("/v1/password-resets", csrfToken, { username }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ status: string }>("/v1/auth/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
  publishFlow: (csrfToken: string, name: string, program: unknown) =>
    authedJSON<FlowVersion>("/v1/flows", csrfToken, { name, program }),
  listFlowVersions: () => request<{ flow_versions: FlowVersion[] }>("/v1/flows"),
  getFlowVersion: (flowVersionId: string) =>
    request<PublishedFlow>(`/v1/flows/${encodeURIComponent(flowVersionId)}`),
  listAssets: () => request<{ assets: Asset[] }>("/v1/assets"),
  registerAsset: (csrfToken: string, assetId: string) =>
    authedJSON<Asset>("/v1/assets", csrfToken, { asset_id: assetId }),
  listVoices: () => request<{ voices: Voice[] }>("/v1/voices"),
  registerVoice: (csrfToken: string, voice: string, language: string) =>
    authedJSON<Voice>("/v1/voices", csrfToken, { voice, language }),
  placeAdhocCall: (
    csrfToken: string,
    flowVersionId: string,
    destination: string,
    trunkRef: string,
    callerId?: string,
  ) =>
    authedJSON<AdhocCall>("/v1/adhoc-calls", csrfToken, {
      flow_version_id: flowVersionId,
      destination,
      trunk_ref: trunkRef,
      caller_id: callerId ?? "",
    }),
  getAdhocCall: (adhocCallId: string) =>
    request<AdhocCall>(`/v1/adhoc-calls/${encodeURIComponent(adhocCallId)}`),
};
